import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { TEST_IDS } from "@/constants/testIds";
import {
  LayoutDashboard, CalendarRange, ListTodo, Gavel, LineChart,
  Settings as SettingsIcon, LogOut, Users, CreditCard, BadgeIndianRupee,
  Search, Command as CommandIcon, PanelLeftClose, PanelLeftOpen, Menu, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Toaster } from "@/components/ui/sonner";
import HelpAssistant from "@/components/HelpAssistant";
import OnboardingTour from "@/components/OnboardingTour";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CommandPalette from "@/components/CommandPalette";

const GROUPS = [
  {
    key: "nav.workspace",
    items: [
      { to: "/", key: "nav.dashboard", icon: LayoutDashboard, testid: TEST_IDS.navDashboard, end: true },
      { to: "/meetings", key: "nav.meetings", icon: CalendarRange, testid: TEST_IDS.navMeetings },
      { to: "/insights", key: "nav.insights", icon: LineChart, testid: "nav-insights" },
    ],
  },
  {
    key: "nav.execution",
    items: [
      { to: "/tasks", key: "nav.tasks", icon: ListTodo, testid: TEST_IDS.navTasks },
      { to: "/decisions", key: "nav.decisions", icon: Gavel, testid: TEST_IDS.navDecisions },
    ],
  },
  {
    key: "nav.settings",
    items: [
      { to: "/billing", key: "nav.billing", icon: CreditCard, testid: TEST_IDS.navBilling },
      { to: "/settings", key: "nav.settings", icon: SettingsIcon, testid: TEST_IDS.navSettings },
    ],
  },
];

const ADMIN_NAV = [
  { to: "/admin/users", key: "nav.users", icon: Users, testid: TEST_IDS.navAdminUsers },
  { to: "/platform-admin/billing", key: "nav.platformBilling", icon: BadgeIndianRupee, testid: TEST_IDS.navAdminBilling },
];

function NavItem({ item, collapsed, t, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      onClick={onNavigate}
      title={collapsed ? t(item.key) : undefined}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-xl text-sm font-semibold transition-colors",
          collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5",
          isActive
            ? "bg-secondary text-secondary-foreground"
            : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground"
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full transition-opacity",
              isActive ? "opacity-100" : "opacity-0"
            )}
            style={{ background: "hsl(var(--cyan))" }}
          />
          <item.icon className="w-[17px] h-[17px] shrink-0" strokeWidth={1.8} />
          {!collapsed && <span className="truncate">{t(item.key)}</span>}
        </>
      )}
    </NavLink>
  );
}

export default function Shell() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("meetiq_nav_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("meetiq_nav_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const initials = (user?.name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  const sidebarInner = (onNavigate) => (
    <>
      <div className={cn("border-b border-border", collapsed ? "px-3 py-5" : "px-5 py-5")}>
        <Link to="/" data-testid="shell-logo-link" onClick={onNavigate} className="block">
          {collapsed ? (
            <div className="w-10 h-10 rounded-2xl grid place-items-center shadow-beam mx-auto" style={{ background: "hsl(var(--navy))" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2 L20 15 H13 Z" fill="hsl(var(--cyan))" />
                <path d="M12 2 L4 15 H11 Z" fill="#ffffff" fillOpacity="0.92" />
                <path d="M9 17 H15 L13.5 22 H10.5 Z" fill="hsl(var(--gold))" />
              </svg>
            </div>
          ) : (
            <BrandMark testid="shell-brand" />
          )}
        </Link>
      </div>

      <nav className={cn("flex-1 overflow-y-auto yl-scroll py-5 space-y-6", collapsed ? "px-2" : "px-3")}>
        {GROUPS.map((g) => (
          <div key={g.key} className="space-y-1">
            {!collapsed && (
              <div className="px-3 pb-1.5 eyebrow text-[9px] text-muted-foreground/70">{t(g.key)}</div>
            )}
            {g.items.map((item) => (
              <NavItem key={item.to} item={item} collapsed={collapsed} t={t} onNavigate={onNavigate} />
            ))}
          </div>
        ))}

        {user?.role === "Admin" && (
          <div className="space-y-1">
            {!collapsed && (
              <div className="px-3 pb-1.5 eyebrow text-[9px] text-muted-foreground/70">{t("nav.admin")}</div>
            )}
            {ADMIN_NAV.map((item) => (
              <NavItem key={item.to} item={item} collapsed={collapsed} t={t} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </nav>

      <div className={cn("border-t border-border", collapsed ? "p-2 space-y-2" : "p-4 space-y-3")}>
        {!collapsed && (
          <div className="flex items-center gap-3 rounded-2xl bg-secondary/50 p-3" data-testid="shell-user-card">
            <div
              className="w-9 h-9 rounded-xl grid place-items-center text-[11px] font-bold text-white shrink-0"
              style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold truncate" title={user?.name}>{user?.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.role}</div>
            </div>
          </div>
        )}
        <button
          data-testid={TEST_IDS.logoutBtn}
          onClick={logout}
          title={t("action.signOut")}
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-2.5 text-sm font-semibold",
            "text-foreground/80 hover:text-destructive hover:border-destructive/40 transition-colors"
          )}
        >
          <LogOut className="w-4 h-4" strokeWidth={1.8} />
          {!collapsed && t("action.signOut")}
        </button>
        <button
          data-testid="shell-collapse-btn"
          onClick={() => setCollapsed((v) => !v)}
          className="hidden md:inline-flex w-full items-center justify-center gap-2 rounded-xl py-2 text-[11px] font-semibold text-muted-foreground hover:text-primary transition-colors"
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4" strokeWidth={1.8} /> : <><PanelLeftClose className="w-4 h-4" strokeWidth={1.8} /> Collapse</>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside
        data-testid={TEST_IDS.shellSidebar}
        className={cn(
          "shrink-0 border-r border-border bg-card/40 hidden md:flex flex-col transition-[width] duration-300",
          collapsed ? "w-[72px]" : "w-[252px]"
        )}
      >
        {sidebarInner(undefined)}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="shell-mobile-nav">
          <button
            type="button"
            aria-label="Close navigation"
            data-testid="shell-mobile-backdrop"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
          />
          <aside className="relative w-[272px] max-w-[82vw] bg-card border-r border-border flex flex-col yl-fade-in">
            <button
              type="button"
              data-testid="shell-mobile-close"
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full border border-border grid place-items-center text-foreground/70 hover:text-primary transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
            {sidebarInner(() => setMobileOpen(false))}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          data-testid="shell-topbar"
          className="yl-beam sticky top-0 z-30 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center gap-3 px-4 sm:px-6"
        >
          <button
            type="button"
            data-testid="shell-mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-9 h-9 rounded-full border border-border grid place-items-center text-foreground/70 shrink-0"
            aria-label="Open navigation"
          >
            <Menu className="w-4 h-4" strokeWidth={1.8} />
          </button>

          <button
            type="button"
            data-testid="global-search-trigger"
            onClick={() => setPaletteOpen(true)}
            className="group flex-1 min-w-0 max-w-lg inline-flex items-center gap-2.5 h-10 px-3.5 rounded-full border border-border bg-card text-left text-sm text-muted-foreground hover:border-primary/50 transition-colors"
          >
            <Search className="w-4 h-4 shrink-0 group-hover:text-primary transition-colors" strokeWidth={1.8} />
            <span className="truncate flex-1 text-xs sm:text-sm">{t("shell.searchHint")}</span>
            <span className="hidden sm:inline-flex items-center gap-1 rounded-md border border-border bg-secondary/70 px-1.5 py-0.5 font-mono text-[10px] text-foreground/60 shrink-0">
              <CommandIcon className="w-3 h-3" strokeWidth={2} />K
            </span>
          </button>

          <div className="flex-1 hidden lg:flex items-center justify-center gap-2 min-w-0">
            <span className="eyebrow text-[9px] text-muted-foreground/80 truncate">{t("shell.tagline")}</span>
          </div>

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>
            <ThemeToggle />
            <button
              type="button"
              data-testid="shell-profile-btn"
              onClick={() => navigate("/settings")}
              className="w-9 h-9 rounded-full grid place-items-center text-[11px] font-bold text-white hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
              title={user?.email}
            >
              {initials}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto yl-scroll">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <Toaster position="top-right" />
      <HelpAssistant />
      <OnboardingTour isAdmin={user?.role === "Admin"} />
    </div>
  );
}
